import { Language } from '@nxt-insurance/constants'

import { CALENDLY_STAGING_LINK } from '@flows/configs/shared/mgaLight/constants'
import type { AnyStepConfiguration } from '@flows/core/types/stepTypes'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'

import { type IncomeProtectionStepParams, successSchema } from './types'

const getCalendlyPrimaryLink = (params: IncomeProtectionStepParams) => {
  if (params.environment === 'production') {
    return params.getCurrentLanguage() === Language.De
      ? 'https://calendly.com/getsafe-leben-gesundheit/leben-gesundheit'
      : 'https://calendly.com/getsafe-leben-gesundheit/life-health'
  }

  return CALENDLY_STAGING_LINK
}

const getCalendlyAlternativeLink = (params: IncomeProtectionStepParams) => {
  if (params.environment === 'production') {
    return params.getCurrentLanguage() === Language.De
      ? 'https://calendly.com/getsafe-leben-gesundheit/leben-gesundheit-2'
      : 'https://calendly.com/getsafe-leben-gesundheit/life-health-2'
  }

  return CALENDLY_STAGING_LINK
}

export const success = (params: IncomeProtectionStepParams) => {
  return {
    schema: successSchema,
    stepContent: [
      {
        type: 'animation',
        width: 140,
        height: 140,
        source: require('@ui-kit/assets/animations/check.json') as string,
      },
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.success.title',
        },
        condition: {
          type: 'conditions',
          comparisonType: 'allOf',
          conditions: [
            {
              type: 'value',
              valuePath: params.helperFunctions.getParamPath('illness'),
              expectedValue: 'no',
            },
            {
              type: 'conditions',
              comparisonType: 'anyOf',
              conditions: [
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('employment'),
                  equalityType: 'notEquals',
                  expectedValue: 'student',
                },
                {
                  type: 'value',
                  value: params.getCurrentLanguage(),
                  equalityType: 'notEquals',
                  expectedValue: Language.En,
                },
              ],
            },
          ],
        },
      },
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.success.title_alternate',
        },
        condition: {
          type: 'conditions',
          comparisonType: 'anyOf',
          conditions: [
            {
              type: 'value',
              valuePath: params.helperFunctions.getParamPath('illness'),
              equalityType: 'notEquals',
              expectedValue: 'no',
            },
            {
              type: 'conditions',
              comparisonType: 'allOf',
              conditions: [
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('employment'),
                  expectedValue: 'student',
                },
                {
                  type: 'value',
                  value: params.getCurrentLanguage(),
                  expectedValue: Language.En,
                },
              ],
            },
          ],
        },
      },
      {
        type: 'text',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.success.text',
        },
        condition: {
          type: 'conditions',
          comparisonType: 'allOf',
          conditions: [
            {
              type: 'value',
              valuePath: params.helperFunctions.getHelperPath('affiliateSource'),
              equalityType: 'notEquals',
              expectedValue: 'Check24',
            },
            {
              type: 'value',
              valuePath: params.helperFunctions.getParamPath('illness'),
              expectedValue: 'no',
            },
            {
              type: 'conditions',
              comparisonType: 'anyOf',
              conditions: [
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('employment'),
                  equalityType: 'notEquals',
                  expectedValue: 'student',
                },
                {
                  type: 'value',
                  value: params.getCurrentLanguage(),
                  equalityType: 'notEquals',
                  expectedValue: Language.En,
                },
              ],
            },
          ],
        },
      },
      {
        type: 'text',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.success.text_alternate',
        },
        condition: {
          type: 'conditions',
          comparisonType: 'allOf',
          conditions: [
            {
              type: 'value',
              valuePath: params.helperFunctions.getHelperPath('affiliateSource'),
              equalityType: 'notEquals',
              expectedValue: 'Check24',
            },
            {
              type: 'conditions',
              comparisonType: 'anyOf',
              conditions: [
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('illness'),
                  equalityType: 'notEquals',
                  expectedValue: 'no',
                },
                {
                  type: 'conditions',
                  comparisonType: 'allOf',
                  conditions: [
                    {
                      type: 'value',
                      valuePath: params.helperFunctions.getParamPath('employment'),
                      expectedValue: 'student',
                    },
                    {
                      type: 'value',
                      value: params.getCurrentLanguage(),
                      expectedValue: Language.En,
                    },
                  ],
                },
              ],
            },
          ],
        },
      },
      {
        type: 'text',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_incomeProtection:steps.success.text_check24',
        },
        condition: {
          type: 'conditions',
          comparisonType: 'allOf',
          conditions: [
            {
              type: 'value',
              valuePath: params.helperFunctions.getHelperPath('affiliateSource'),
              expectedValue: 'Check24',
            },
            {
              type: 'value',
              valuePath: params.helperFunctions.getParamPath('illness'),
              expectedValue: 'no',
            },
            {
              type: 'conditions',
              comparisonType: 'anyOf',
              conditions: [
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('employment'),
                  equalityType: 'notEquals',
                  expectedValue: 'student',
                },
                {
                  type: 'value',
                  value: params.getCurrentLanguage(),
                  equalityType: 'notEquals',
                  expectedValue: Language.En,
                },
              ],
            },
          ],
        },
      },
      {
        type: 'calendly',
        title: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.success.calendly.title',
        },
        subtitle: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.success.calendly.subtitle',
        },
        link: getCalendlyPrimaryLink(params),
        bgColor: 'grey100',
        condition: {
          type: 'conditions',
          comparisonType: 'anyOf',
          conditions: [
            {
              type: 'value',
              valuePath: params.helperFunctions.getHelperPath('affiliateSource'),
              expectedValue: 'Check24',
            },
            {
              type: 'conditions',
              comparisonType: 'allOf',
              conditions: [
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('illness'),
                  expectedValue: 'no',
                },
                {
                  type: 'conditions',
                  comparisonType: 'anyOf',
                  conditions: [
                    {
                      type: 'value',
                      valuePath: params.helperFunctions.getParamPath('employment'),
                      equalityType: 'notEquals',
                      expectedValue: 'student',
                    },
                    {
                      type: 'value',
                      value: params.getCurrentLanguage(),
                      equalityType: 'notEquals',
                      expectedValue: Language.En,
                    },
                  ],
                },
                {
                  type: 'conditions',
                  comparisonType: 'anyOf',
                  conditions: [
                    {
                      type: 'value',
                      valuePath: params.helperFunctions.getParamPath('income'),
                      expectedValue: 'more_than_74k',
                    },
                    {
                      type: 'value',
                      valuePath: params.helperFunctions.getParamPath('income'),
                      expectedValue: '50k_to_74k',
                    },
                    {
                      type: 'value',
                      valuePath: params.helperFunctions.getParamPath('income'),
                      expectedValue: '30k_to_50k',
                    },
                  ],
                },
              ],
            },
          ],
        },
      },
      {
        type: 'calendly',
        title: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.success.calendly.title',
        },
        subtitle: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.success.calendly.subtitle',
        },
        link: getCalendlyAlternativeLink(params),
        bgColor: 'grey100',
        condition: {
          type: 'conditions',
          comparisonType: 'allOf',
          conditions: [
            {
              type: 'value',
              valuePath: params.helperFunctions.getHelperPath('affiliateSource'),
              equalityType: 'notEquals',
              expectedValue: 'Check24',
            },
            {
              type: 'conditions',
              comparisonType: 'allOf',
              conditions: [
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('illness'),
                  expectedValue: 'no',
                },
                {
                  type: 'conditions',
                  comparisonType: 'anyOf',
                  conditions: [
                    {
                      type: 'value',
                      valuePath: params.helperFunctions.getParamPath('employment'),
                      equalityType: 'notEquals',
                      expectedValue: 'student',
                    },
                    {
                      type: 'value',
                      value: params.getCurrentLanguage(),
                      equalityType: 'notEquals',
                      expectedValue: Language.En,
                    },
                  ],
                },
                {
                  type: 'conditions',
                  comparisonType: 'anyOf',
                  conditions: [
                    {
                      type: 'value',
                      valuePath: params.helperFunctions.getParamPath('income'),
                      expectedValue: 'less_than_30k',
                    },
                  ],
                },
              ],
            },
          ],
        },
      },
    ],
    subFlow: SubFlow.Common,
    onSubmit: {
      type: 'dispatchedHook',
      callbackConfig: {
        name: 'updateConsultationRequest',
        inputs: [],
        outputs: [],
      },
    },
    hideBackButton: true,
    buttonLabel: {
      type: 'translatableText',
      translationKey: 'sharedFlows_incomeProtection:steps.success.proceedButtonLabel',
    },
    footer: { hideFooter: true },
    nextStep: 'appointment_details',
  } as const satisfies AnyStepConfiguration
}
